/**
 * Validates fields of a form.
 * Member 'errors' contain all the error messages. Where each key is the field name and the value
 * is an array of string error messages.
 */
export default class Validator {
	errors: { [key: string]: Array<string> };

	constructor(errors: { [key: string]: Array<string> }) {
		this.errors = errors;
	}

	addErrorMessage(key: string, message: string) {
		if (!this.errors[key]) {
			this.errors[key] = [];
		}
		this.errors[key].push(message);
	}

	validateStringLength(errorKey: string, value: string | undefined, min: number, max: number = Infinity) {
		if (value === undefined) return;
		if (value.length >= min && value.length <= max) return;

		this.addErrorMessage(errorKey, `Length of "${errorKey}" should be between ${min} and ${max}`);
	}

	validateStringSuffix(errorKey: string, value: string | undefined, suffix: string, caseSensitive: boolean = false) {
		if (value === undefined) return;
		if (caseSensitive && value.trim().endsWith(suffix)) return;
		if (!caseSensitive && value.toLocaleLowerCase().trim().endsWith(suffix.toLocaleLowerCase())) return;

		this.addErrorMessage(errorKey, `"${errorKey}" should end with "${suffix}"`);
	}

	validateStringPrefix(errorKey: string, value: string | undefined, prefix: string, caseSensitive: boolean = false) {
		if (value === undefined) return;
		if (caseSensitive && value.trim().startsWith(prefix)) return;
		if (!caseSensitive && value.toLocaleLowerCase().trim().startsWith(prefix.toLocaleLowerCase())) return;

		this.addErrorMessage(errorKey, `"${errorKey}" should start with "${prefix}"`);
	}

	validateURL(errorKey: string, value: string | undefined, required: boolean = true) {
		if (value === undefined) return;
		// If not required don't check URL if empty string
		if (!required && value.length <= 0) return true;

		const urlRegex =
			'^(?!mailto:)(?:(?:https)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
		const url = new RegExp(urlRegex, 'i');
		if (value.length < 2083 && url.test(value)) return;

		this.addErrorMessage(errorKey, `"${errorKey}" is not a valid URL. It should be an HTTPS website.`);
	}

	// Like validateURL but allows mailto addresses
	validateURI(errorKey: string, value: string | undefined, required: boolean = true) {
		if (value === undefined) return;
		// If not required don't check URL if empty string
		if (!required && value.length <= 0) return true;

		const urlRegex =
			'^(mailto:.*@.*)|(?:(?:https)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
		const url = new RegExp(urlRegex, 'i');
		if (value.length < 2083 && url.test(value)) return;

		this.addErrorMessage(errorKey, `"${errorKey}" is not a valid URI (should be an HTTPS website or a mailto address)`);
	}

	validateStringContains(errorKey: string, value: string | undefined, contains: Array<string>) {
		if (value === undefined) return;
		if (contains.every((c) => value.includes(c))) return;

		this.addErrorMessage(errorKey, `"${errorKey}" should contain the following string(s): ${contains}`);
	}

	validateIntRange(errorKey: string, value: number | undefined, min: number, max: number) {
		if (value === undefined) return;
		if (value >= min && value <= max) return;

		this.addErrorMessage(errorKey, `"${errorKey}" should be between ${min} and ${max}`);
	}

	validateUUID(errorKey: string, value: string | undefined) {
		if (value === undefined) return;

		const uuidRegex = '^[0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12}$';
		const uuid = new RegExp(uuidRegex, 'i');
		if (uuid.test(value)) return;

		this.addErrorMessage(errorKey, `"${errorKey}" is not a valid UUID`);
	}

	validateBoolean(errorKey: string, value: boolean | undefined, expected: boolean) {
		if (value === undefined || value === expected) return;

		this.addErrorMessage(errorKey, `"${errorKey}" should be ${expected}`);
	}
}
